import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import type { GalleryImage } from '@/shared/api';
import { DRAG_TYPES, type DraggedGalleryTile } from '@/shared/types';

interface DraggableGalleryTileProps {
  image: GalleryImage;
  index: number;
  /** Пока идёт загрузка или сохраняется порядок, плитки не таскаются. */
  disabled: boolean;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  /** Вызывается при любом окончании перетаскивания — сохранять ли, решает сетка. */
  onDragEnd: () => void;
  onDelete: (image: GalleryImage) => void;
}

/**
 * Одно фото в сетке ленты. Плитки одного размера, поэтому, в отличие от строк
 * врачей, без проверки середины: фото уступает место, как только на него
 * навели перетаскиваемое.
 *
 * Окончание перетаскивания передаётся всегда, а не только при отпускании на
 * другой плитке: между плитками есть просветы, а после последней — пустые
 * клетки, и отпущенное там фото иначе осталось бы на новом месте только на
 * экране.
 */
export const DraggableGalleryTile: React.FC<DraggableGalleryTileProps> = ({
  image,
  index,
  disabled,
  onMove,
  onDragEnd,
  onDelete
}) => {
  const ref = useRef<HTMLLIElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPES.GALLERY_TILE,
    item: (): DraggedGalleryTile => ({ type: DRAG_TYPES.GALLERY_TILE, id: image.id, index }),
    canDrag: !disabled,
    end: () => {
      onDragEnd();
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });

  const [, drop] = useDrop({
    accept: DRAG_TYPES.GALLERY_TILE,
    hover: (item: DraggedGalleryTile) => {
      if (item.index === index) {
        return;
      }
      onMove(item.index, index);
      item.index = index;
    }
  });

  drag(drop(ref));

  return (
    <li
      ref={ref}
      className={`relative ${isDragging ? 'opacity-40' : 'opacity-100'} transition-opacity duration-200`}
      style={{ cursor: disabled ? 'default' : 'grab' }}
    >
      {/*
        draggable={false}: иначе браузер тянет саму картинку и кладёт её в
        перетаскивание как файл — зона загрузки под сеткой подсвечивается и
        может принять фото как новое.
      */}
      <img
        src={image.imageUrl}
        alt={`Фото ${index + 1} в ленте`}
        loading="lazy"
        draggable={false}
        className="w-full h-36 object-cover rounded-lg border border-base-300"
      />
      <span className="badge badge-neutral absolute top-2 left-2">{index + 1}</span>
      <button
        type="button"
        className="btn btn-error btn-xs absolute top-2 right-2"
        onClick={() => onDelete(image)}
        disabled={disabled}
        aria-label={`Удалить фото ${index + 1}`}
      >
        <i className="fas fa-trash"></i>
      </button>
    </li>
  );
};
